import { newId, type ISODate } from '@keel/core';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { datePresetRange, type DatePreset } from './date-presets';
import type { TxStatus } from './labels';

/** Register filter (maps onto `transactions.list` input). Amount bounds compare against |amount|. */
export interface TxFilter {
  readonly preset: DatePreset;
  readonly from?: ISODate;
  readonly to?: ISODate;
  readonly accountId?: string;
  readonly categoryIds: readonly string[];
  readonly payeeIds: readonly string[];
  readonly tagIds: readonly string[];
  readonly status: readonly TxStatus[];
  readonly minCents?: number;
  readonly maxCents?: number;
  readonly q: string;
  readonly unreviewedOnly: boolean;
}

export const EMPTY_FILTER: TxFilter = { preset: 'all', categoryIds: [], payeeIds: [], tagIds: [], status: [], q: '', unreviewedOnly: false };

/** Number of active (non-default) filter facets, for the "Filters (3)" label. */
export const activeFilterCount = (f: TxFilter): number =>
  [f.preset !== 'all', f.categoryIds.length, f.payeeIds.length, f.tagIds.length, f.status.length,
    f.minCents !== undefined || f.maxCents !== undefined, f.q.trim(), f.unreviewedOnly].filter(Boolean).length;

/** `transactions.list` input for a filter (dates resolved from the preset with the entity FY). */
export const toListInput = (entityId: string, f: TxFilter, today: ISODate, fyStartMonth = 7, fyStartDay = 1) => {
  const range = datePresetRange(f.preset, today, fyStartMonth, fyStartDay, { from: f.from, to: f.to });
  return {
    entityId,
    ...(f.accountId ? { accountId: f.accountId } : {}),
    ...(range.from ? { from: range.from } : {}),
    ...(range.to ? { to: range.to } : {}),
    ...(f.q.trim() ? { q: f.q.trim() } : {}),
    ...(f.categoryIds.length ? { categoryIds: [...f.categoryIds] } : {}),
    ...(f.payeeIds.length ? { payeeIds: [...f.payeeIds] } : {}),
    ...(f.tagIds.length ? { tagIds: [...f.tagIds] } : {}),
    ...(f.status.length ? { status: [...f.status] } : {}),
    ...(f.unreviewedOnly ? { unreviewedOnly: true } : {}),
    ...(f.minCents !== undefined ? { minCents: f.minCents } : {}),
    ...(f.maxCents !== undefined ? { maxCents: f.maxCents } : {}),
  };
};

export interface SavedFilter { readonly id: string; readonly entityId: string; readonly name: string; readonly filter: TxFilter }

interface SavedFiltersState {
  saved: SavedFilter[];
  save: (entityId: string, name: string, filter: TxFilter) => SavedFilter;
  remove: (id: string) => void;
}

/** Saved register filters, per browser (localStorage `keel:saved-filters`), scoped by entity. */
export const useSavedFilters = create<SavedFiltersState>()(
  persist(
    (set) => ({
      saved: [],
      save: (entityId, name, filter) => {
        const row = { id: newId(), entityId, name, filter };
        set((s) => ({ saved: [...s.saved.filter((x) => !(x.entityId === entityId && x.name === name)), row] }));
        return row;
      },
      remove: (id) => set((s) => ({ saved: s.saved.filter((x) => x.id !== id) })),
    }),
    { name: 'keel:saved-filters', version: 1, storage: createJSONStorage(() => localStorage) },
  ),
);
